import { redactText } from "@/lib/errorRedaction";

/**
 * CSV export for `public.frontend_error_events`.
 *
 * Events are already redacted client-side before they are stored, but the
 * export re-applies redaction as a defence-in-depth pass: rows written by
 * older builds (or by any future path that bypasses the logger) must never
 * leave the admin UI with raw emails/tokens inside message/stack/source.
 */

export interface ExportableErrorEvent {
  id: string;
  created_at: string;
  error_type: string;
  message: string | null;
  route: string | null;
  source?: string | null;
  lineno?: number | null;
  colno?: number | null;
  stack?: string | null;
  release?: string | null;
  app_version?: string | null;
  build_commit?: string | null;
  fingerprint?: string | null;
  session_id?: string | null;
}

export const ERROR_CSV_COLUMNS = [
  "created_at",
  "error_type",
  "route",
  "message",
  "source",
  "lineno",
  "colno",
  "fingerprint",
  "release",
  "app_version",
  "build_commit",
  "session_id",
  "stack",
] as const;

/** RFC 4180 quoting + defusing spreadsheet formula injection. */
export function csvCell(value: unknown): string {
  if (value == null) return "";
  let text = String(value);
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
}

function redactedRow(event: ExportableErrorEvent): Record<string, string> {
  return {
    created_at: event.created_at ?? "",
    error_type: event.error_type ?? "",
    route: redactText(event.route) ?? "",
    message: redactText(event.message) ?? "",
    source: redactText(event.source) ?? "",
    lineno: event.lineno == null ? "" : String(event.lineno),
    colno: event.colno == null ? "" : String(event.colno),
    fingerprint: event.fingerprint ?? "",
    release: event.release ?? "",
    app_version: event.app_version ?? "",
    build_commit: event.build_commit ?? "",
    session_id: event.session_id ?? "",
    // Newlines are legal inside a quoted CSV field, but collapsing them keeps
    // one event on one spreadsheet row.
    stack: (redactText(event.stack) ?? "").replace(/\r?\n/g, " ⏎ "),
  };
}

/** Build the full CSV document (header + rows) for a set of events. */
export function buildErrorEventsCsv(events: ExportableErrorEvent[]): string {
  const header = ERROR_CSV_COLUMNS.join(",");
  const lines = events.map((event) => {
    const row = redactedRow(event);
    return ERROR_CSV_COLUMNS.map((column) => csvCell(row[column])).join(",");
  });
  return [header, ...lines].join("\r\n") + "\r\n";
}

/** Deterministic, filter-aware filename so downloads don't overwrite each other. */
export function errorEventsCsvFilename(
  filters: Record<string, string | undefined>,
  now = new Date(),
): string {
  const stamp = now.toISOString().replace(/[:.]/g, "-").slice(0, 19);
  const parts = Object.entries(filters)
    .filter(([, v]) => v)
    .map(([k, v]) => `${k}-${String(v).replace(/[^a-z0-9]+/gi, "_").slice(0, 24)}`);
  return ["frontend-errors", ...parts, stamp].join("_") + ".csv";
}

/** Trigger a browser download for the given CSV text. */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
